import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Modal,
  ModalFooter,
  Textarea,
} from "@/components/ui";
import { formatMoney, parseMoneyInputToCents } from "@/lib/money";

const AMOUNT_ID = "receipt-admin-adjust-amount";
const REASON_ID = "receipt-admin-adjust-reason";

interface ReceiptAdminAdjustModalProps {
  currentTotalCents: number;
  isSubmitting: boolean;
  onSubmit: (newTotalCents: number, reason: string | null) => void;
  onClose: () => void;
}

export function ReceiptAdminAdjustModal({
  currentTotalCents,
  isSubmitting,
  onSubmit,
  onClose,
}: ReceiptAdminAdjustModalProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("bookings");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string>();

  const handleSubmit = () => {
    const cents = parseMoneyInputToCents(amount);
    if (cents == null || cents <= 0) {
      setError(t("adminReceiptAdjust.invalidAmount"));
      return;
    }
    if (cents === currentTotalCents) {
      setError(t("adminReceiptAdjust.sameAmount"));
      return;
    }
    setError(undefined);
    onSubmit(cents, reason.trim() ? reason.trim() : null);
  };

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen
      onClose={onClose}
      title={t("adminReceiptAdjust.title")}
      description={t("adminReceiptAdjust.description", {
        current: formatMoney(currentTotalCents),
      })}
      size="sm"
      priority="high"
      showCloseButton={!isSubmitting}
      footer={
        <ModalFooter>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            {t("adminReceiptAdjust.submit")}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t("common:cancel")}
          </Button>
        </ModalFooter>
      }
    >
      <Input
        id={AMOUNT_ID}
        label={t("adminReceiptAdjust.amountLabel")}
        type="text"
        inputMode="decimal"
        value={amount}
        onChange={(e) => {
          setAmount(e.target.value);
          setError(undefined);
        }}
        placeholder={t("adminReceiptAdjust.amountPlaceholder")}
        error={error}
      />
      <div className="mt-4">
        <Textarea
          id={REASON_ID}
          label={t("adminReceiptAdjust.reasonLabel")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("adminReceiptAdjust.reasonPlaceholder")}
          rows={3}
        />
      </div>
    </Modal>
  );
}
